import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Loader2, Plus, Send, Eye, BarChart3, FileText, SlidersHorizontal,
  CheckCircle2, AlertCircle, ChevronRight, Users, Search, X, ArrowLeft,
} from 'lucide-react'
import { apiGet, cachedGet, postJson, invalidateCache } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { useEphemeralState } from '../../lib/persist.js'
import { toISO, fmtThaiMonth, MONTHS_TH_SHORT } from '../../lib/datetime.js'
import { isHR, canEditEvaluationRules, canViewEvaluatorDetail } from '../../lib/role.js'
import { nameInitial } from '../../lib/text.js'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'
import { relabelBranch } from '../../lib/asset.js'
import { pickableBranches, normalizeBranchId } from '../../lib/branch.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import Pager from '../../components/Pager.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'
import EvaluationFormBuilder from '../../components/employee/EvaluationFormBuilder.jsx'
import EvaluationRules from '../../components/employee/EvaluationRules.jsx'
import EvaluationAssignWizard from '../../components/employee/EvaluationAssignWizard.jsx'
import EvaluationDetail from '../../components/employee/EvaluationDetail.jsx'
import EvaluationCycleDetail from '../../components/employee/EvaluationCycleDetail.jsx'
import {
  fmtPercent, relationLabel, scoreTone, TONE_CLASS, resultProgress,
  aggregateGroupScores, totalFromGroups,
} from '../../lib/evaluation.js'

// /manager/employee/evaluation — the HR + Manager side of ระบบประเมินพนักงาน.
//
// Four sub-views. The last two are HR/Admin only, matching the hrOnly pattern the salary and
// หักประจำ tabs already use; the /api/hr/evaluation/form and /rule proxies refuse a plain
// Manager server-side too, so hiding them here is presentation, not protection.
//
// What a Manager sees in รายงาน is decided in Xano, not here: manager-level subjects
// (ผู้จัดการ / CEO) never reach the client at all.

// `rules` uses a WIDER gate than `forms`: เกณฑ์ is company-wide performance policy, which
// is a GM call as much as an HR one, while authoring the instrument stays HR/Admin.
const SUBVIEWS = [
  { key: 'report', label: 'รายงาน',          icon: BarChart3 },
  { key: 'cycles', label: 'รอบประเมิน',      icon: Send },
  { key: 'forms',  label: 'ชุดคำถามประเมิน', icon: FileText,          gate: 'hr' },
  { key: 'rules',  label: 'เกณฑ์',           icon: SlidersHorizontal, gate: 'rules' },
]

export default function EmployeeEvaluation() {
  const me = useMe()
  const hr = isHR(me)
  const canRules = canEditEvaluationRules(me)
  const views = SUBVIEWS.filter((v) => {
    if (v.gate === 'hr') return hr
    if (v.gate === 'rules') return canRules
    return true
  })
  const [view, setView] = useState('report')

  return (
    <div>
      <div className="flex items-center gap-1 mb-5">
        {views.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13.5px] font-medium transition-colors ${
              view === key ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Icon size={14} strokeWidth={1.75} />
            {label}
          </button>
        ))}
      </div>

      {view === 'report' && <ReportView hr={hr} />}
      {view === 'cycles' && <CyclesView hr={hr} />}
      {view === 'forms'  && hr && <EvaluationFormBuilder />}
      {view === 'rules'  && canRules && <EvaluationRules />}
    </div>
  )
}

/* ── รายงาน ───────────────────────────────────────────────────────────────── */

const REPORT_PER_PAGE = 15

// The report is EMPLOYEE-first, not round-first.
//
// It used to open on a single <select> of every รอบประเมิน ever run — 40 options and growing,
// each labelled with a free-text title — and showed one round at a time. Answering "how is
// อาร์ต doing" meant knowing which round to look in, and comparing two rounds meant leaving
// and coming back. Rounds are how the paperwork is ORGANISED; the person is what anyone
// actually wants to read about.
//
// So: one row per evaluated employee, their คะแนนแต่ละกลุ่ม folded across every round the
// filter selects → open the person to see those rounds listed → open a round for the
// per-evaluator breakdown. The filter drives the folding, so narrowing to ก.ย. + ทดลองงาน
// re-scores every line against just those rounds.
//
// GET /evaluation/result takes no cycle_id now and returns the lot (112 rows when this was
// built), so the whole pivot is one request plus the round list, and every filter is instant.
function ReportView({ hr }) {
  const me = useMe()
  // GM / HR / Admin may open one ผู้ประเมิน's paper. A plain branch Manager stops at the
  // round's group averages. Peer names are redacted by Xano for everyone but HR/Admin
  // regardless of this flag — see canViewEvaluatorDetail.
  const canDrill = canViewEvaluatorDetail(me)

  const [cycles, setCycles] = useState([])
  const [branches, setBranches] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmPublish, setConfirmPublish] = useState(false)

  // ประเมิน's own person, not the roster→payroll tabs' — see EmployeeFilter.jsx. The
  // picker lists the whole active roster, not just people with a result: someone never
  // evaluated, or whom the other filters exclude, lands on the empty state, which is
  // the honest answer.
  const [empId, setEmpId] = useEmployeeFilter('evaluation')
  const [year, setYear] = useEphemeralState('emp:ev:rep:year', '')
  const [month, setMonth] = useEphemeralState('emp:ev:rep:month', '')
  const [type, setType] = useEphemeralState('emp:ev:rep:type', '')
  // The EMPLOYEE's branch (evaluation_result.branch_id, denormalised from Employee when the
  // score was rolled up) — not the round's scope. "Show me อารีย์'s people" is the question
  // this answers; a round's own branch is a different fact, shown on each round at level 2.
  const [branchId, setBranchId] = useEphemeralState('emp:ev:rep:branch', '')
  const [page, setPage] = useState(1)

  const [openEmp, setOpenEmp] = useState(null)    // subject_employee_id being read
  const [detailId, setDetailId] = useState(null)  // evaluation_result id being opened

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const [c, r, b] = await Promise.all([
        apiGet('/api/hr/evaluation/cycle'),
        // No cycle_id: every round at once. The proxy strips a 0 so this cannot
        // accidentally become "rounds whose id is 0".
        apiGet('/api/hr/evaluation/result'),
        cachedGet('/api/branches'),
      ])
      setCycles(Array.isArray(c) ? c : [])
      setRows(Array.isArray(r) ? r : [])
      setBranches(Array.isArray(b) ? b : (b?.items || []))
    } catch (e) {
      setErr(e?.message || 'โหลดรายงานไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [empId, year, month, type, branchId])

  const cycleById = useMemo(() => new Map(cycles.map((c) => [c.id, c])), [cycles])
  const branchName = useMemo(() => {
    const m = new Map(branches.map((b) => [b.id, relabelBranch(b.name)]))
    return (id) => (id ? (m.get(id) || `สาขา ${id}`) : 'ทุกสาขา')
  }, [branches])

  // Only rounds that actually produced a result can appear, so the pickers are built from
  // those rather than from every round ever created.
  const scoredCycles = useMemo(() => {
    const ids = new Set(rows.map((r) => r.cycle_id))
    return cycles.filter((c) => ids.has(c.id))
  }, [rows, cycles])
  const yearOpts = useMemo(
    () => [...new Set(scoredCycles.map(issueYear).filter(Boolean))].sort().reverse(),
    [scoredCycles],
  )
  const typeOpts = useMemo(
    () => [...new Set(scoredCycles.map((c) => c.type).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th')),
    [scoredCycles],
  )
  // Built from the result rows, so every branch that has someone in this report is offered —
  // including one pickableBranches hides from creation pickers.
  const branchOpts = useMemo(() => {
    const ids = [...new Set(rows.map((r) => r.branch_id).filter(Boolean))]
    return ids
      .map((id) => ({ id, label: branchName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'th'))
  }, [rows, branchName])

  // Result rows surviving the filter. Year/month/type are properties of the ROUND, so each row
  // is judged by the cycle it belongs to; a row whose cycle is missing is dropped rather than
  // shown undated.
  const scoped = useMemo(() => rows.filter((r) => {
    const c = cycleById.get(r.cycle_id)
    if (!c) return false
    if (type && c.type !== type) return false
    if (year && issueYear(c) !== year) return false
    if (month && issueMonth(c) !== month) return false
    if (branchId && Number(r.branch_id) !== Number(branchId)) return false
    if (empId && Number(r.subject_employee_id) !== Number(empId)) return false
    return true
  }), [rows, cycleById, type, year, month, branchId, empId])

  // One line per person, their rounds newest first, scores folded across them.
  const people = useMemo(() => {
    const m = new Map()
    for (const r of scoped) {
      const k = r.subject_employee_id
      if (!m.has(k)) {
        m.set(k, {
          id: k,
          name: r.name,
          nickname: r.nickname,
          code: r.code,
          image: r.image,
          position: r.position,
          rounds: [],
        })
      }
      m.get(k).rounds.push(r)
    }
    const out = []
    for (const p of m.values()) {
      // Newest first — aggregateGroupScores takes weight_percent from the first round that
      // carries each relation, so this ordering is what makes the current เกณฑ์ win.
      p.rounds.sort((a, b) => Number(cycleById.get(b.cycle_id)?.created_at || 0)
        - Number(cycleById.get(a.cycle_id)?.created_at || 0))
      // Branch as of their most recent round in view. Someone who transferred mid-year has a
      // different branch_id on older rows, and the newest is the one worth showing beside a
      // name — the filter still matches them under either, because it filters rows not people.
      p.branch_id = p.rounds[0]?.branch_id ?? null
      p.groups = aggregateGroupScores(p.rounds.map((r) => r.group_scores))
      p.total = totalFromGroups(p.groups)
      p.expected = p.groups.reduce((s, g) => s + Number(g.n_expected || 0), 0)
      p.submitted = p.groups.reduce((s, g) => s + Number(g.n_submitted || 0), 0)
      p.unpublished = p.rounds.filter((r) => !r.published).length
      out.push(p)
    }
    // Highest score first, and anyone still unscored last — an em dash sorted among the
    // numbers reads as a bad result rather than as a round still coming in.
    return out.sort((a, b) => {
      if (a.total == null && b.total == null) return 0
      if (a.total == null) return 1
      if (b.total == null) return -1
      return b.total - a.total
    })
  }, [scoped, cycleById])

  const pageTotal = Math.max(1, Math.ceil(people.length / REPORT_PER_PAGE))
  const safePage = Math.min(page, pageTotal)
  const pageRows = useMemo(
    () => people.slice((safePage - 1) * REPORT_PER_PAGE, safePage * REPORT_PER_PAGE),
    [people, safePage],
  )
  const hasFilter = Boolean(empId || year || month || type || branchId)
  const clearAll = () => { setEmpId(''); setYear(''); setMonth(''); setType(''); setBranchId('') }

  // Publish acts on EXACTLY the results in view, never a whole round by implication: the ids
  // are passed explicitly, grouped by the cycle the endpoint needs. Filter down to one person
  // and only that person's results are released.
  const publishPlan = useMemo(() => {
    const byCycle = new Map()
    let n = 0
    for (const p of people) {
      for (const r of p.rounds) {
        if (r.published || !r.id) continue
        if (!byCycle.has(r.cycle_id)) byCycle.set(r.cycle_id, [])
        byCycle.get(r.cycle_id).push(r.id)
        n += 1
      }
    }
    return { byCycle, count: n, people: people.filter((p) => p.unpublished > 0).length }
  }, [people])

  async function publishInView() {
    setConfirmPublish(false)
    setBusy(true)
    setErr('')
    try {
      for (const [cycle_id, result_ids] of publishPlan.byCycle) {
        await postJson('/api/hr/evaluation/result/publish', { cycle_id, result_ids })
      }
      await load()
    } catch (e) {
      setErr(e?.message || 'เผยแพร่ไม่สำเร็จ')
    } finally {
      setBusy(false)
    }
  }

  const person = openEmp == null ? null : people.find((p) => p.id === openEmp)

  /* ── level 3: one round, opened up ──────────────────────────────────────── */
  if (detailId) {
    return <EvaluationDetail resultId={detailId} onBack={() => setDetailId(null)} />
  }

  /* ── level 2: one person's rounds ───────────────────────────────────────── */
  if (person) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpenEmp(null)}
          className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-3"
        >
          <ArrowLeft size={14} strokeWidth={1.75} /> กลับไปรายงาน
        </button>

        <div className="flex items-center gap-3 mb-4">
          <EmployeeTrigger employeeId={person.id} className="items-center gap-3">
            <EmployeeAvatar
              src={person.image}
              name={person.nickname || person.name}
              className="w-11 h-11 rounded-xl"
              textClass="text-[15px]"
            />
            <div className="min-w-0">
              <div className="text-[17px] font-semibold text-slate-900 tracking-tight truncate">
                {person.nickname || person.name}
              </div>
              <div className="text-[12.5px] text-slate-500 truncate">
                {[person.position, person.code].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          </EmployeeTrigger>
          <div className="ml-auto text-right flex-none">
            <div className="text-[26px] font-semibold text-slate-900 tabular-nums leading-none">
              {fmtPercent(person.total)}
            </div>
            <div className="text-[11.5px] text-slate-400 mt-1">
              เฉลี่ย {person.rounds.length} รอบ · ตอบกลับ {person.submitted}/{person.expected}
            </div>
          </div>
        </div>

        <GroupChips groups={person.groups} className="mb-4" />

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-50">
          {person.rounds.map((r) => {
            const c = cycleById.get(r.cycle_id)
            const p = resultProgress(r)
            const openable = canDrill && r.id
            return (
              <button
                key={r.id ?? `${r.cycle_id}-${r.subject_employee_id}`}
                type="button"
                disabled={!openable}
                onClick={() => openable && setDetailId(r.id)}
                className={`w-full text-left px-5 py-4 flex items-start gap-4 transition-colors ${
                  openable ? 'hover:bg-brand-50/40 cursor-pointer' : 'cursor-default'}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium text-slate-900 truncate">
                    {c?.title || `รอบ #${r.cycle_id}`}
                  </div>
                  <div className="text-[12px] text-slate-400 mt-0.5">
                    {[c?.type, branchName(c?.branch_id), fmtThaiMonth(c?.created_at)].filter(Boolean).join(' · ')}
                  </div>
                  <GroupChips groups={r.group_scores} className="mt-2" />
                </div>
                <div className="text-right flex-none">
                  <div className="text-[15px] font-semibold text-slate-900 tabular-nums">{fmtPercent(r.total_percent)}</div>
                  <span className={`inline-flex mt-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${
                    p.complete ? TONE_CLASS.emerald : TONE_CLASS.amber
                  }`}>
                    {p.submitted}/{p.expected}
                  </span>
                </div>
                <div className="flex-none pt-1">
                  {r.published
                    ? <CheckCircle2 size={14} className="text-emerald-500" title="เผยแพร่แล้ว" />
                    : openable ? <ChevronRight size={15} className="text-slate-300" /> : <span className="w-[15px] inline-block" />}
                </div>
              </button>
            )
          })}
        </div>

        {!canDrill && (
          <p className="text-[12.5px] text-slate-400 mt-3">
            รายละเอียดรายผู้ประเมินเปิดให้เฉพาะ GM / ฝ่ายบุคคล / ผู้ดูแลระบบ
          </p>
        )}
      </div>
    )
  }

  /* ── level 1: everyone ──────────────────────────────────────────────────── */
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <div className="min-w-[220px] flex-1 max-w-[320px]">
          <EmployeePicker value={empId} onChange={setEmpId} allowAll className="w-full" />
        </div>
        <select value={year} onChange={(e) => setYear(e.target.value)} className={SELECT} title="ปีที่ออกแบบประเมิน">
          <option value="">ทุกปี</option>
          {yearOpts.map((y) => <option key={y} value={y}>{Number(y) + 543}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={SELECT} title="เดือนที่ออกแบบประเมิน">
          <option value="">ทุกเดือน</option>
          {MONTHS_TH_SHORT.map((label, i) => (
            <option key={label} value={String(i + 1).padStart(2, '0')}>{label}</option>
          ))}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className={SELECT} title="ประเภทรอบประเมิน">
          <option value="">ทุกประเภท</option>
          {typeOpts.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT} title="สาขาของพนักงาน">
          <option value="">ทุกสาขา</option>
          {branchOpts.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
        {hasFilter && (
          <button
            type="button"
            onClick={clearAll}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100"
          >
            <X size={14} /> ล้าง
          </button>
        )}

        {hr && publishPlan.count > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirmPublish(true)}
            className="ml-auto px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} strokeWidth={1.75} />}
            เผยแพร่ผล ({publishPlan.count})
          </button>
        )}
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-[12px] font-medium text-slate-400 border-b border-slate-100">
                <th className="text-left px-4 py-2.5">พนักงาน</th>
                <th className="text-left px-4 py-2.5">ตำแหน่ง</th>
                <th className="text-left px-4 py-2.5 whitespace-nowrap">สาขา</th>
                <th className="text-left px-4 py-2.5">คะแนนแต่ละกลุ่ม</th>
                <th className="text-center px-4 py-2.5 whitespace-nowrap">รอบ</th>
                <th className="text-right px-4 py-2.5 whitespace-nowrap">คะแนนรวม</th>
                <th className="text-center px-4 py-2.5 whitespace-nowrap">ความคืบหน้า</th>
                <th className="px-2" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                  <Loader2 size={16} className="animate-spin inline mr-2" />กำลังโหลด…
                </td></tr>
              )}
              {/* !err: a failed load must not also claim there are no results. */}
              {!loading && !err && !rows.length && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                  ยังไม่มีผลประเมิน — ไปที่แท็บ “รอบประเมิน” เพื่อเลือกผู้ถูกประเมินและส่ง
                </td></tr>
              )}
              {!loading && rows.length > 0 && !people.length && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                  ไม่พบผลประเมินที่ตรงกับตัวกรอง
                </td></tr>
              )}
              {!loading && pageRows.map((p) => {
                const complete = p.expected > 0 && p.submitted >= p.expected
                return (
                  <tr
                    key={p.id}
                    className="border-b border-slate-50 hover:bg-brand-50/40 cursor-pointer"
                    onClick={() => setOpenEmp(p.id)}
                  >
                    {/* The row opens the PERSON'S rounds; the photo/name opens their staff
                        record — a score is read against who they are and how long they have
                        been here. */}
                    <td className="px-4 py-3">
                      <EmployeeTrigger employeeId={p.id} className="items-center gap-2.5">
                        <EmployeeAvatar src={p.image} name={p.nickname || p.name} />
                        <div className="min-w-0">
                          <div className="text-slate-900 truncate">{p.nickname || p.name}</div>
                          <div className="text-[11.5px] text-slate-400 truncate">{p.code}</div>
                        </div>
                      </EmployeeTrigger>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{p.position || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {p.branch_id ? branchName(p.branch_id) : '—'}
                    </td>
                    <td className="px-4 py-3"><GroupChips groups={p.groups} /></td>
                    <td className="px-4 py-3 text-center text-slate-500 tabular-nums">{p.rounds.length}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                      {fmtPercent(p.total)}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${
                        complete ? TONE_CLASS.emerald : TONE_CLASS.amber
                      }`}>
                        {p.submitted}/{p.expected}
                      </span>
                    </td>
                    <td className="px-2">
                      {p.unpublished === 0
                        ? <CheckCircle2 size={14} className="text-emerald-500" title="เผยแพร่แล้วทุกรอบ" />
                        : <ChevronRight size={15} className="text-slate-300" />}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && rows.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-[13px] text-slate-500">
          <span>
            ทั้งหมด {people.length.toLocaleString()} คน · {scoped.length.toLocaleString()} รอบ
          </span>
          <Pager page={safePage} pageTotal={pageTotal} onChange={setPage} />
        </div>
      )}

      {confirmPublish && (
        <ConfirmDialog
          title="เผยแพร่ผลประเมิน?"
          message={`พนักงาน ${publishPlan.people} คน (${publishPlan.count} รอบ) จะเห็นคะแนนของตัวเองในเมนู “ประเมิน” ทันที · เผยแพร่เฉพาะรายการที่อยู่ในตัวกรองนี้เท่านั้น`}
          confirmLabel="เผยแพร่"
          onConfirm={publishInView}
          onCancel={() => setConfirmPublish(false)}
        />
      )}
    </div>
  )
}

// An employee's photo, or their initial when there is no usable photo.
//
// Employee.image holds plenty of URLs that no longer resolve — Bubble-era links and files
// since removed from Wasabi — and a plain <img> renders those as a broken-image glyph, which
// reads as "this app is broken" rather than "this person has no photo". Same shape as
// DoctorAvatar in RevenueReport / Reimbursement / Dentist; there is no shared avatar
// component in this codebase yet, and inventing one would touch a dozen files.
//
// The reset on `src` matters: paging swaps who this slot is showing, and a reused instance
// would otherwise keep the previous person's failure and draw an initial over a photo that
// loads perfectly well.
function EmployeeAvatar({ src, name, className = 'w-8 h-8 rounded-lg', textClass = 'text-[12px]' }) {
  const [broken, setBroken] = useState(false)
  useEffect(() => { setBroken(false) }, [src])
  const box = `bg-slate-100 text-slate-500 flex items-center justify-center overflow-hidden flex-none ring-1 ring-slate-200/70 ${className}`
  return (
    <div className={box}>
      {src && !broken
        ? <img src={src} alt="" onError={() => setBroken(true)} className="w-full h-full object-cover" />
        : <span className={`${textClass} font-semibold`}>{nameInitial(name)}</span>}
    </div>
  )
}

// คะแนนแต่ละกลุ่ม as pills. One place, because the same set is drawn three times now (the
// employee line, the person header, each of their rounds) and they must stay identical —
// a group reading 88% in one and 88.4% in another is read as two different facts.
function GroupChips({ groups, className = '' }) {
  const list = (groups || []).filter((g) => g && g.relation)
  if (!list.length) return <div className={`text-[12px] text-slate-300 ${className}`}>—</div>
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {list.map((g) => (
        <span
          key={g.relation}
          title={`${relationLabel(g.relation)} · น้ำหนัก ${g.weight_percent}% · ตอบกลับ ${g.n_submitted}/${g.n_expected} คน`}
          className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${TONE_CLASS[scoreTone(g.avg_percent)]}`}
        >
          {relationLabel(g.relation)} {fmtPercent(g.avg_percent, 0)}
        </span>
      ))}
    </div>
  )
}

/* ── รอบประเมิน ───────────────────────────────────────────────────────────── */

const CYCLES_PER_PAGE = 12
const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

// ทั้งหมด first and default: this list is the entry point to the whole feature, and a
// status filter that survives silently is how someone concludes their round was deleted.
const CYCLE_STATUS_OPTS = [
  { key: '', label: 'ทั้งหมด' },
  { key: 'open', label: 'เปิดอยู่' },
  { key: 'draft', label: 'ฉบับร่าง' },
  { key: 'closed', label: 'ปิดแล้ว' },
]

// The "no branch" sentinel. A round with branch_id null is the company-wide ทุกสาขา round,
// which is a real scope to filter ON — distinct from the picker's "don't filter by branch"
// option. A plain '' cannot carry both meanings, hence the string.
const BRANCH_NONE = 'none'

// WHEN A ROUND WAS ISSUED — the month its แบบประเมิน went out.
//
// evaluation_cycle has no dispatched_at column, so this reads created_at. Measured against
// the real dispatch times that is the same day every time (cycle 8: created 08:38, first
// questionnaire written 08:45; cycle 56: 06:02 → 06:06), so at month granularity the two are
// interchangeable and no schema change is warranted. A round left in draft for weeks before
// ส่ง is the one case they could differ.
//
// period_start is deliberately NOT the source: that is the window of WORK being judged, which
// routinely sits months before the paperwork — round 79 judges มิ.ย.–ก.ย. and went out in ก.ย.
//
// Everything goes through toISO: created_at is epoch MILLISECONDS, and slicing one as a string
// prints the first ten digits of the epoch (the trap that shipped on the certificate register).
const issueISO = (c) => toISO(c?.created_at)
const issueYear = (c) => issueISO(c).slice(0, 4)
const issueMonth = (c) => issueISO(c).slice(5, 7)

function CyclesView() {
  const me = useMe()
  const hr = isHR(me)
  const [cycles, setCycles] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [creating, setCreating] = useState(false)
  const [wizardCycle, setWizardCycle] = useState(null)
  const [detailCycle, setDetailCycle] = useState(null)

  // Ephemeral, not persisted: the filters survive opening a round and coming back (the
  // actual annoyance) but reset on a hard reload, so nobody inherits yesterday's filter.
  const [search, setSearch] = useEphemeralState('emp:ev:cycles:search', '')
  const [status, setStatus] = useEphemeralState('emp:ev:cycles:status', '')
  const [type, setType] = useEphemeralState('emp:ev:cycles:type', '')
  const [branchId, setBranchId] = useEphemeralState('emp:ev:cycles:branch', '')
  // Both hold the CE/ISO form ('2026', '09') even though the UI shows พ.ศ. — comparing the
  // same strings toISO already produces keeps the ±543 in one place, the label.
  const [year, setYear] = useEphemeralState('emp:ev:cycles:year', '')
  const [month, setMonth] = useEphemeralState('emp:ev:cycles:month', '')
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [c, b] = await Promise.all([
        apiGet('/api/hr/evaluation/cycle'),
        cachedGet('/api/branches'),
      ])
      setCycles(Array.isArray(c) ? c : [])
      setBranches(Array.isArray(b) ? b : (b?.items || []))
    } catch (e) {
      setErr(e?.message || 'โหลดรอบประเมินไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const branchName = useMemo(() => {
    const m = new Map(branches.map((b) => [b.id, relabelBranch(b.name)]))
    return (id) => (id ? (m.get(id) || `สาขา ${id}`) : 'ทุกสาขา')
  }, [branches])

  const [debounced, setDebounced] = useState(search)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250)
    return () => clearTimeout(t)
  }, [search])
  useEffect(() => { setPage(1) }, [debounced, status, type, branchId, year, month])

  // Both pickers are built from the rounds actually loaded, not from the branch master or the
  // type enum. That way every round in the list is reachable by a filter — including one
  // scoped to สนามเป้า, which pickableBranches deliberately hides from creation pickers.
  const branchOpts = useMemo(() => {
    const ids = [...new Set(cycles.map((c) => c.branch_id).filter(Boolean))]
    return ids
      .map((id) => ({ id, label: branchName(id) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'th'))
  }, [cycles, branchName])
  const hasCompanyWide = useMemo(() => cycles.some((c) => !c.branch_id), [cycles])
  const typeOpts = useMemo(
    () => [...new Set(cycles.map((c) => c.type).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th')),
    [cycles],
  )
  // Years come from the data (newest first); months are the fixed twelve. A month with no
  // rounds in it simply lands on the empty state, which is a truthful answer to the question.
  const yearOpts = useMemo(
    () => [...new Set(cycles.map(issueYear).filter(Boolean))].sort().reverse(),
    [cycles],
  )

  // Branch name is part of the haystack on purpose: typing "สีลม" should find that branch's
  // rounds even though the title is free text and often omits it.
  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase()
    return cycles.filter((c) => {
      if (status && c.status !== status) return false
      if (type && c.type !== type) return false
      if (year && issueYear(c) !== year) return false
      if (month && issueMonth(c) !== month) return false
      if (branchId === BRANCH_NONE) { if (c.branch_id) return false }
      else if (branchId && Number(c.branch_id) !== Number(branchId)) return false
      if (q) {
        const hay = [c.title, c.type, branchName(c.branch_id), fmtThaiMonth(c.created_at)]
          .filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [cycles, debounced, status, type, branchId, year, month, branchName])

  // safePage, not page: closing a round or clearing a filter can shrink the list under the
  // page you are standing on, and slicing past the end would show an empty table with no pager.
  const pageTotal = Math.max(1, Math.ceil(filtered.length / CYCLES_PER_PAGE))
  const safePage = Math.min(page, pageTotal)
  const pageRows = useMemo(
    () => filtered.slice((safePage - 1) * CYCLES_PER_PAGE, safePage * CYCLES_PER_PAGE),
    [filtered, safePage],
  )
  const hasFilter = Boolean(search || status || type || branchId || year || month)

  // Opening a round lands on its DETAIL, not on the assignment wizard. A round that has
  // already been dispatched has content worth seeing, and the old behaviour — straight into
  // step 1 with nothing ticked — was indistinguishable from a round never sent.
  if (wizardCycle) {
    return (
      <EvaluationAssignWizard
        cycle={wizardCycle}
        onBack={() => { setWizardCycle(null); load() }}
      />
    )
  }

  if (detailCycle) {
    return (
      <EvaluationCycleDetail
        cycle={detailCycle}
        branches={branches}
        onBack={() => { setDetailCycle(null); load() }}
        onAssign={() => { setWizardCycle(detailCycle); setDetailCycle(null) }}
      />
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[13px] text-slate-500">
          รอบประเมินคือชุดของแบบประเมินที่ส่งออกไปพร้อมกัน · สร้างเป็นฉบับร่างก่อน แล้วค่อยตรวจรายชื่อผู้ประเมินก่อนส่ง
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm inline-flex items-center gap-2 flex-none"
        >
          <Plus size={15} strokeWidth={2} /> สร้างรอบประเมิน
        </button>
      </div>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อรอบ / สาขา / เดือนที่ออก…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10"
          />
        </div>
        <select value={year} onChange={(e) => setYear(e.target.value)} className={SELECT} title="ปีที่ออกแบบประเมิน">
          <option value="">ทุกปี</option>
          {yearOpts.map((y) => <option key={y} value={y}>{Number(y) + 543}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={SELECT} title="เดือนที่ออกแบบประเมิน">
          <option value="">ทุกเดือน</option>
          {MONTHS_TH_SHORT.map((label, i) => (
            <option key={label} value={String(i + 1).padStart(2, '0')}>{label}</option>
          ))}
        </select>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
          <option value="">ทุกสาขา</option>
          {hasCompanyWide && <option value={BRANCH_NONE}>รอบทั้งบริษัท</option>}
          {branchOpts.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className={SELECT}>
          <option value="">ทุกประเภท</option>
          {typeOpts.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {hasFilter && (
          <button
            type="button"
            onClick={() => {
              setSearch(''); setStatus(''); setType(''); setBranchId(''); setYear(''); setMonth('')
            }}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100"
          >
            <X size={14} /> ล้าง
          </button>
        )}
        <div className="ml-auto inline-flex rounded-xl ring-1 ring-slate-200 bg-white p-0.5">
          {CYCLE_STATUS_OPTS.map((o) => (
            <button
              key={o.key || 'all'}
              type="button"
              onClick={() => setStatus(o.key)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition ${
                status === o.key ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-50">
        {loading && (
          <div className="px-4 py-12 text-center text-slate-400">
            <Loader2 size={16} className="animate-spin inline mr-2" />กำลังโหลด…
          </div>
        )}
        {!loading && !err && !cycles.length && (
          <div className="px-4 py-12 text-center text-slate-400">ยังไม่มีรอบประเมิน</div>
        )}
        {!loading && cycles.length > 0 && !filtered.length && (
          <div className="px-4 py-12 text-center text-slate-400">ไม่พบรอบประเมินที่ตรงกับตัวกรอง</div>
        )}
        {!loading && pageRows.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setDetailCycle(c)}
            className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-brand-50/40 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium text-slate-900 truncate">{c.title}</div>
              <div className="text-[12px] text-slate-400 mt-0.5">
                {c.type} · {branchName(c.branch_id)}
                {c.period_start ? ` · ${c.period_start} ถึง ${c.period_end || '-'}` : ''}
              </div>
            </div>
            {/* Its own column rather than another clause on the subtitle: these line up
                down the list so a month can be found by eye, and it keeps the พ.ศ. year
                away from the ค.ศ. period dates immediately above it. */}
            <span
              className="text-[12px] text-slate-500 tabular-nums flex-none w-[68px] text-right"
              title="เดือนที่ออกแบบประเมิน"
            >
              {fmtThaiMonth(c.created_at)}
            </span>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${
              c.status === 'open' ? TONE_CLASS.emerald : c.status === 'closed' ? TONE_CLASS.slate : TONE_CLASS.amber
            }`}>
              {c.status === 'open' ? 'เปิดอยู่' : c.status === 'closed' ? 'ปิดแล้ว' : 'ฉบับร่าง'}
            </span>
            <ChevronRight size={15} className="text-slate-300 flex-none" />
          </button>
        ))}
      </div>

      {!loading && cycles.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-[13px] text-slate-500">
          <span>
            ทั้งหมด {filtered.length.toLocaleString()} รอบ
            {hasFilter && filtered.length !== cycles.length ? ` จาก ${cycles.length.toLocaleString()}` : ''}
          </span>
          <Pager page={safePage} pageTotal={pageTotal} onChange={setPage} />
        </div>
      )}

      {creating && (
        <CycleCreateModal
          branches={branches}
          hr={hr}
          myBranchId={me?.branch_id}
          onClose={() => setCreating(false)}
          onCreated={(c) => { setCreating(false); invalidateCache('/api/hr/evaluation'); setWizardCycle(c) }}
        />
      )}
    </div>
  )
}

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

function CycleCreateModal({ branches, hr, myBranchId, onClose, onCreated }) {
  const [title, setTitle] = useState('')
  const [type, setType] = useState('ประจำปี')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [closes, setCloses] = useState('')
  // A Manager may open a round for ANY branch (owner's decision, 2026-08-31) — branches
  // lend staff to each other constantly, so the person running the round is often not
  // sitting at the branch being judged. Their own branch is only the DEFAULT, not a cage.
  // What stays HR/Admin-only is "ทุกสาขา", the company-wide round: that is a different
  // act from picking a branch, and Xano refuses a null branch_id from a plain Manager.
  // normalizeBranchId keeps the default off HQ (สนามเป้า), which rosters nobody.
  const [branchId, setBranchId] = useState(hr ? 0 : normalizeBranchId(myBranchId))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function save() {
    setBusy(true)
    setErr('')
    try {
      const body = {
        title,
        type,
        period_start: start || null,
        period_end: end || null,
        closes_at: closes ? new Date(closes).getTime() : null,
        branch_id: branchId ? Number(branchId) : null,
      }
      const c = await postJson('/api/hr/evaluation/cycle', body)
      onCreated(c)
    } catch (e) {
      setErr(e?.message || 'สร้างรอบประเมินไม่สำเร็จ')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl animate-ss-pop-in">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-[16px] font-semibold text-slate-900">สร้างรอบประเมิน</h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={LABEL}>ชื่อรอบ</label>
            <input className={FIELD} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="เช่น ประเมินประจำปี 2569" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>ประเภท</label>
              <select className={FIELD} value={type} onChange={(e) => setType(e.target.value)}>
                {['ประจำปี', 'ทดลองงาน', 'เฉพาะกิจ'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>สาขา</label>
              {/* pickableBranches drops สนามเป้า (HQ, id 1) — it runs no clinic and rosters
                  nobody, so a round scoped to it can never find a single subject. It was in
                  this list once and produced exactly that: an empty step 1. */}
              <select
                className={FIELD}
                value={branchId}
                onChange={(e) => setBranchId(Number(e.target.value))}
              >
                {hr && <option value={0}>ทุกสาขา</option>}
                {pickableBranches(branches).map((b) => (
                  <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>
                ))}
              </select>
              {!hr && (
                <p className="mt-1 text-[11.5px] text-slate-400">
                  ตั้งต้นเป็นสาขาของคุณ · เลือกสาขาอื่นได้
                </p>
              )}
            </div>
          </div>
          {/* Two dates that get confused constantly, so each says what it is FOR. The period
              is the work being judged; closes_at is the hand-in deadline. */}
          <div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>ช่วงที่ประเมิน — เริ่ม</label>
                <input type="date" className={FIELD} value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div>
                <label className={LABEL}>ถึง</label>
                <input type="date" className={FIELD} value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
            <p className="text-[11.5px] text-slate-400 mt-1">
              ช่วงเวลา “การทำงาน” ที่ให้ผู้ประเมินนึกถึง — ไม่ใช่กำหนดส่ง · ใช้หาทันตแพทย์ที่ลงตารางที่สาขาในช่วงนี้ด้วย
            </p>
          </div>
          <div>
            <label className={LABEL}>ปิดรับแบบประเมิน (ไม่บังคับ)</label>
            <input type="datetime-local" className={FIELD} value={closes} onChange={(e) => setCloses(e.target.value)} />
            <p className="text-[11.5px] text-slate-400 mt-1">
              กำหนด “ส่ง” ของผู้ประเมิน — เลยเวลานี้จะส่งไม่ได้ · ปกติตั้งหลังวันสิ้นสุดช่วงที่ประเมิน
              {start && end && closes && closes.slice(0, 10) < end
                ? <span className="block text-amber-600 mt-0.5">⚠ ตอนนี้ตั้งไว้ก่อนวันสิ้นสุดช่วงที่ประเมิน — ผู้ประเมินจะส่งไม่ทัน</span>
                : null}
            </p>
          </div>
          {err && (
            <div className="flex items-start gap-2 text-[13px] text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5">
              <AlertCircle size={14} className="mt-0.5 flex-none" />{err}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-[14px] font-medium text-slate-600 hover:bg-slate-50">
            ยกเลิก
          </button>
          <button
            type="button"
            disabled={busy || !title.trim() || !start || !end}
            onClick={save}
            className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} strokeWidth={1.75} />}
            ถัดไป — เลือกผู้ถูกประเมิน
          </button>
        </div>
      </div>
    </div>
  )
}
